import { Chip, ChipProps } from '@mui/material';
import { ReactNode } from 'react';

export type PillKind = 'success' | 'warning' | 'error' | 'info' | 'neutral';

interface StatusPillProps extends Omit<ChipProps, 'color' | 'variant'> {
  kind?: PillKind;
  label: ReactNode;
}

/**
 * Theme-token-driven status pill used everywhere on the transaction
 * details surface. All colours flow from theme.palette so each client's
 * brand palette renders consistently — zero hard-coded hex.
 */
const StatusPill: React.FC<StatusPillProps> = ({ kind = 'neutral', label, sx, ...rest }) => {
  // Filled chip style — matches the AI Detection badges (solid severity.main
  // background, white text). Used by AI Summary, Live Driver header, and
  // every other status pill on the transaction details surface so the chip
  // language stays uniform across the page.
  const palette = ((): { bg: string; fg: string; border?: string } => {
    switch (kind) {
      case 'success':
        return { bg: 'success.main', fg: '#fff' };
      case 'warning':
        return { bg: 'warning.main', fg: '#fff' };
      case 'error':
        return { bg: 'error.main', fg: '#fff' };
      case 'info':
        return { bg: 'info.main', fg: '#fff' };
      default:
        return { bg: 'grey.200', fg: 'text.primary', border: 'grey.300' };
    }
  })();

  return (
    <Chip
      size="small"
      label={label}
      sx={{
        bgcolor: palette.bg,
        color: palette.fg,
        fontWeight: 600,
        fontSize: '0.7rem',
        height: 22,
        letterSpacing: 0.2,
        border: palette.border ? '1px solid' : 'none',
        borderColor: palette.border,
        ...sx
      }}
      {...rest}
    />
  );
};

export default StatusPill;
